import { useMutation } from '@apollo/client';
import type { CommunityGroup } from '@communities/community-groups/domain';
import { UPDATE_COMMUNITY_GROUP, GET_COMMUNITY_GROUP } from '@communities/community-groups/graphql';
import { transformErrors } from '@core/adapters/errors/transformError';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import { useCallback, useState } from 'react';

export interface UpdateCommunityGroupInput {
  securityGroups?: {
    admin: string[];
    moderator: string[];
    user: string[];
  };
}

export interface UpdateCommunityGroupResponse {
  updateCommunityGroup: CommunityGroup;
}

export function useUpdateCommunityGroup(communityGroupId?: string) {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [updateCommunityGroupMutation, { loading }] = useMutation<UpdateCommunityGroupResponse>(UPDATE_COMMUNITY_GROUP, {
    refetchQueries: communityGroupId ? [{ query: GET_COMMUNITY_GROUP, variables: { id: communityGroupId } }] : [],
    awaitRefetchQueries: true,
  });

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  const updateCommunityGroup = async (id: string, input: UpdateCommunityGroupInput): Promise<boolean> => {
    if (!id) {
      throw new Error('Community Group ID is required');
    }

    try {
      track('UpdateCommunityGroupInitiated', {
        communityGroupId: id,
      });

      await updateCommunityGroupMutation({
        variables: { id, input },
      });

      track('UpdateCommunityGroupSucceeded', {
        communityGroupId: id,
      });

      return true;
    } catch (error) {
      track('UpdateCommunityGroupFailed', {
        communityGroupId: id,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      setErrors(transformErrors(error));

      return false;
    }
  };

  return {
    updateCommunityGroup,
    loading,
    errors,
    resetErrors,
  };
}

export default useUpdateCommunityGroup;
